import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Shield, Loader2 } from 'lucide-react';
import { useAuth, sanitizeLoginNext, LOGIN_NEXT_PARAM } from '@onvice/portal-shell-api';
import { useTheme } from '../context/ThemeContext';
import { LucideByName } from '@onvice/portal-ui';

function TenantIcon({ name, size = 32, className = '' }: { name?: string; size?: number; className?: string }) {
  return <LucideByName name={name} fallback={Shield} size={size} className={className} />;
}

export default function LoginPage() {
  const { isAuthenticated, loading: authLoading, login, verify2FA } = useAuth();
  const { tenant } = useTheme();
  // Rücksprungziel aus ?next= (nur relative Portal-Pfade, siehe login-redirect.ts).
  // Gilt für Passwort-Login UND 2FA — beide setzen isAuthenticated, das Navigate
  // unten greift dann.
  const [searchParams] = useSearchParams();
  const nextTarget = sanitizeLoginNext(searchParams.get(LOGIN_NEXT_PARAM));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to={nextTarget ?? '/'} replace />;

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires2FA && result.pendingToken) {
        setPendingToken(result.pendingToken);
      }
    } catch (err: unknown) {
      setError((err as Error).message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e: FormEvent) => {
    e.preventDefault();
    if (!pendingToken) return;
    setError('');
    setLoading(true);
    try {
      await verify2FA(pendingToken, code, trustDevice);
    } catch (err: unknown) {
      setError((err as Error).message || '2FA-Verifizierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
            <TenantIcon name={tenant.icon} size={32} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Anmelden</h1>
          <p className="text-muted-foreground text-sm mt-1">Bitte gib deine Zugangsdaten ein</p>
        </div>

        {/* Fehler */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Login-Formular oder 2FA */}
        {!pendingToken ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full h-12 px-4 rounded-lg bg-muted border border-default text-foreground placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="name@firma.de"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Passwort</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full h-12 px-4 rounded-lg bg-muted border border-default text-foreground placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Anmelden
            </button>
          </form>
        ) : (
          <form onSubmit={handle2FA} className="space-y-4">
            <p className="text-sm text-muted-foreground">Gib den 6-stelligen Code aus deiner Authenticator-App ein.</p>
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                className="w-full h-12 px-4 rounded-lg bg-muted border border-default text-foreground text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="000000"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="rounded border-default"
              />
              Diesem Gerät vertrauen ({`${30} Tage`})
            </label>
            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full h-12 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              Verifizieren
            </button>
            <button
              type="button"
              onClick={() => { setPendingToken(null); setCode(''); setError(''); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Zurück zum Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
